import {
  OrganizationEntry,
  PersonEntry,
  PersonWrapperEntry,
} from "frontend/contentful/schema/semantics";
import { createContentfulProps } from "frontend/contentful/lib/create-contentful-props";
import { getEntityName } from "../utils/get-entity-name";
import { ArticleFooterAuthorInfoProps } from "design-system/components/pages/article-page/article-footer/article-footer-author-info";
import { useContentful } from "frontend/hooks/use-contentful";
import { ArticleFooterLinkedAuthorProps } from "design-system/components/pages/article-page/article-footer/article-footer-linked-author";
import { documentToString } from "frontend/contentful/lib/document-to-string";
import { createPersonImageProps } from "./media-asset.props";
import { isContentType } from "frontend/contentful/lib/is-content-type";

export const createArticleAuthorProps = createContentfulProps<
  PersonEntry | OrganizationEntry | PersonWrapperEntry,
  ArticleFooterAuthorInfoProps
>(({ entry: entity, createEditAttributes }) => {
  const { getEntryUrl, getEntry } = useContentful();

  const entry = isContentType<PersonWrapperEntry>("personWrapper", entity)
    ? getEntry(entity.fields.person)
    : entity;

  if (!entry) return;

  const link =
    "bioURL" in entry.fields && entry.fields.bioURL
      ? getEntryUrl(entry.fields.bioURL)
      : undefined;

  const props: ArticleFooterAuthorInfoProps = {
    name: getEntityName(entry),
    link,
    email: "email" in entry.fields ? entry.fields.email : undefined,
    phone: "phone" in entry.fields ? entry.fields.phone : undefined,
    editAttributes: {
      name: createEditAttributes({
        entry: entry,
        fieldId: isContentType<PersonEntry>("person", entry)
          ? "firstName"
          : "name",
      }),
      email:
        "email" in entry.fields
          ? createEditAttributes({
              entry: entry,
              fieldId: "email",
            })
          : undefined,
      phone:
        "phone" in entry.fields
          ? createEditAttributes({
              entry: entry,
              fieldId: "phone",
            })
          : undefined,
    },
  };

  return props;
});

export const createArticleLinkedAuthorProps = createContentfulProps<
  PersonEntry | OrganizationEntry | PersonWrapperEntry,
  ArticleFooterLinkedAuthorProps
>(({ entry: entity }) => {
  const { getEntryUrl, getEntityName, getEntry } = useContentful();

  const entry = isContentType<PersonWrapperEntry>("personWrapper", entity)
    ? getEntry(entity.fields.person)
    : entity;

  if (!entry) return;

  const props: ArticleFooterLinkedAuthorProps = {
    name: getEntityName(entry),
    href:
      "bioURL" in entry.fields && entry.fields.bioURL
        ? getEntryUrl(entry.fields.bioURL)
        : undefined,
    titles: isContentType<PersonEntry>("person", entry)
      ? entry.fields.jobTitles
      : undefined,
    bio:
      "bio" in entry.fields && entry.fields.bio
        ? documentToString(entry.fields.bio)
        : undefined,
    image: isContentType<PersonEntry>("person", entry)
      ? createPersonImageProps(entry)?.image
      : undefined,
  };

  return props;
});
